import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl } from "../utils/utils";

// Async thunk for fetching credit data
export const fetchCreditData = createAsyncThunk(
  "credit/fetchCreditData",
  async ({ page = 1, limit = 100, search = "" } = {}, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "list");
      body.append("page", page);
      body.append("limit", limit);
      if (search) {
        body.append("search", search);
      }

      const response = await fetch(baseUrl + "credit.php", {
        method: "POST",
        body: body,
      });

      const res = await response.json();
      if (!response.ok) throw new Error(res.message || "Failed to fetch credit data");
      if (!res.success) return rejectWithValue(res.message || "API returned unsuccessful response");

      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Async thunk for fetching credit details
export const fetchCreditDetails = createAsyncThunk(
  "credit/fetchCreditDetails",
  async (id, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "details");
      body.append("id", id);

      const response = await fetch(baseUrl + "credit.php", {
        method: "POST",
        body: body,
      });

      const res = await response.json();
      
      if (!response.ok) {
        throw new Error(res.message || 'Failed to fetch credit details');
      }
      
      if (!res.success) {
        return rejectWithValue(res.message || 'API returned unsuccessful response');
      }
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const creditSlice = createSlice({
  name: "credit",
  initialState: {
    data: [],
    details: null,
    loading: false,
    detailsLoading: false,
    error: null,
    detailsError: null,
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  },
  reducers: {
    clearCreditData: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
    },
    clearCreditDetails: (state) => {
      state.details = null;
      state.detailsLoading = false;
      state.detailsError = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    updateCreditStatus: (state, action) => {
      const { creditId, status, creditLimit, creditDays } = action.payload;
      
      // Update the main data array (list of applications)
      state.data = state.data.map(app => {
        if (app.id === creditId) {
          // Create updated application with new status in details
          const updatedApp = { ...app };
          
          if (updatedApp.details && updatedApp.details.length > 0) {
            // Update the latest detail entry
            updatedApp.details = updatedApp.details.map((detail, index) => {
              if (index === updatedApp.details.length - 1) { // Only update the latest entry
                return {
                  ...detail,
                  status: status,
                  credit_limit: creditLimit || detail.credit_limit,
                  credit_days: creditDays || detail.credit_days
                };
              }
              return detail;
            });
          } else {
            // Create details array if it doesn't exist
            updatedApp.details = [{
              id: Date.now().toString(),
              ref_id: creditId,
              status: status,
              credit_limit: creditLimit || "0",
              credit_days: creditDays || "0",
              cc_type_limit: "WARN",
              datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
              message: "Status updated"
            }];
          }
          
          return updatedApp;
        }
        return app;
      });

      // Also update the details object if it's currently being viewed
      if (state.details && state.details.id === creditId) {
        if (state.details.details && state.details.details.length > 0) {
          // Update the latest detail entry
          state.details.details = state.details.details.map((detail, index) => {
            if (index === state.details.details.length - 1) {
              return {
                ...detail,
                status: status,
                credit_limit: creditLimit || detail.credit_limit,
                credit_days: creditDays || detail.credit_days
              };
            }
            return detail;
          });
        } else {
          // Create details array if it doesn't exist
          state.details.details = [{
            id: Date.now().toString(),
            ref_id: creditId,
            status: status,
            credit_limit: creditLimit || "0",
            credit_days: creditDays || "0",
            cc_type_limit: "WARN",
            datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
            message: "Status updated"
          }];
        }
      }
    },
    updateCreditDiscounts: (state, action) => {
      const { creditId, discounts } = action.payload;
      
      // Update discounts in the main data array
      state.data = state.data.map(app => {
        if (app.id === creditId) {
          return { ...app, discounts: discounts };
        }
        return app;
      });

      // Update discounts in the details object if currently viewed
      if (state.details && state.details.id === creditId) {
        state.details.discounts = discounts;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreditData.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;

        if (data.success) {
          state.data = data.data || [];
          state.totalItems = data.totalcount || state.data.length;
          state.totalPages = Math.ceil(state.totalItems / 100);
        } else {
          state.error = data.message || "Failed to fetch credit data";
          state.data = [];
        }
      })
      .addCase(fetchCreditData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchCreditDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(fetchCreditDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        const data = action.payload;

        if (data.success) {
          state.details = data.data || null;
        } else {
          state.detailsError = data.message || "Failed to fetch credit details";
          state.details = null;
        }
      })
      .addCase(fetchCreditDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.error.message;
      });
  },
});

export const { 
  clearCreditData, 
  clearCreditDetails, 
  setCurrentPage, 
  updateCreditStatus,
  updateCreditDiscounts
} = creditSlice.actions;

export default creditSlice.reducer;